import {
  AdminBffClient,
  AdminBffMethodNames,
  AdminBffMethods,
  adminBffMethods,
} from '@my-medication/bff-interfaces-ui/infrastructure/admin/repository.types';
import { getSessionId, setCorrelationId } from '@my-medication/shared-components/helpers/datadog.helper';
import { notifyAdminUnauthorized } from '@hooks/useAdminBffApi';
import { v4 as uuidv4 } from 'uuid';

const BASE_URL = '/api/v1/admin';
const TOKEN_STORAGE_KEY = 'rx-token';
const IMPERSONATION_TOKEN_STORAGE_STARTING_VALUE = 'bearer';
const X_CORRELATION_ID_HEADER = 'X-Correlation-ID';
const X_SESSION_ID_HEADER = 'X-Session-ID';

type Fetcher = (url: string, init: RequestInit) => Promise<Response>;

class HttpError extends Error {
  response: { status: number; data: unknown };

  constructor(status: number, data: unknown) {
    super(`Request failed with status code ${status}`);
    this.name = 'HttpError';
    this.response = { status, data };
  }
}

const defaultFetcher: Fetcher = (url, init) => window.fetch(url, init);

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const buildUrl = (endpoint: string, queryParams?: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
  }
  const queryString = params.toString();
  return `${BASE_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;
};

const isImpersonation = (token: string | null): boolean =>
  !!token?.startsWith(IMPERSONATION_TOKEN_STORAGE_STARTING_VALUE);

const createV1Repository = (fetcher: Fetcher, storage: Storage): AdminBffClient => {
  const getToken = (): string | null => storage.getItem(TOKEN_STORAGE_KEY);

  const execute = async <T extends AdminBffMethods, M extends AdminBffMethodNames>({
    method,
    queryParams,
    body,
    locale = 'de',
    refreshTokenCallback,
    isRetry = false,
  }: {
    method: M;
    queryParams?: T[M]['queryType'];
    body?: T[M]['bodyType'];
    locale?: string;
    refreshTokenCallback?: () => Promise<{
      token: string;
      refreshToken: string;
    }>;
    isRetry?: boolean;
  }): Promise<T[M]['returnType']> => {
    const { callMethod, endpoint } = adminBffMethods[method];

    const sessionId = getSessionId();
    const correlationId = uuidv4();
    setCorrelationId(correlationId);
    const url = buildUrl(endpoint, queryParams);
    const token = getToken();
    const init: RequestInit = {
      method: callMethod,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Accept-Language': locale,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        [X_CORRELATION_ID_HEADER]: correlationId,
        ...(isImpersonation(token) && { Authorization: token as string }),
        [X_SESSION_ID_HEADER]: sessionId,
      },
      ...(body && { body: JSON.stringify(body) }),
    };

    const response = await fetcher(url, init);
    const data = await parseResponseBody(response);

    if (!response.ok) {
      const error = new HttpError(response.status, data);
      if (response.status === 401 && refreshTokenCallback && !isRetry) {
        const refreshResult = await refreshTokenCallback();
        if (!refreshResult) {
          notifyAdminUnauthorized();
          throw error;
        }
        return execute({
          method,
          queryParams,
          body,
          locale,
          refreshTokenCallback,
          isRetry: true,
        });
      }

      if (response.status === 401) {
        notifyAdminUnauthorized();
      }

      throw error;
    }

    return data;
  };

  return { execute } as AdminBffClient;
};

const v1RepositoryInstance = createV1Repository(defaultFetcher, window.localStorage);

export { createV1Repository, HttpError, v1RepositoryInstance };
